import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import MDSpinner from "react-md-spinner";
import BillboardCinemaBlock from './BillboardCinemaBlock';
import BillboardSidebarDates from './BillboardSidebarDates';
import BillboardStickyHeader from './BillboardStickyHeader';
import { getAbsoluteUrl, getAreaBillboardPermalink, getStateBillboardPermalink } from '../../utils/urls';
import NoContentMessage from '../common/NoContentMessage';
import Button from '../Button';
import { BillboardDataType } from './BillboardPage';
import BillboardHeading from './BillboardHeading';
import HorizontalScroller from '../common/HorizontalScroller';
import RequireAuthMessage from '../user/RequireAuthMessage';
import { CinemaType } from '../../types/entities';
import PageTitle from '../common/PageTitle';
import CinemaMaintenanceLegend from '../common/CinemaMaintenanceLegend';

interface Props {
  areaType: string;
  areaId: number;
  area: any;
  data: BillboardDataType['cinemas'] | null;
  dates: string[];
  selectedDate: string;
  selectedCinemaId: number | null;
  fav?: boolean;
  filteredData: BillboardDataType['cinemas'] | null;
  isLoading: boolean;
  isUserLogged: boolean;
  favCinemasIds: number[];
  match: any;
  cinemasById: { [id: number]: CinemaType };
  fetch: (areaType: string, areaId: string, date?: string) => void;
  onDateSelect: (date: string) => void;
  onCinemaSelect: (cinemaId: number) => void;
  onCinemasEdit?: () => void;
  onAreaChangeClick?: () => void;
  onFiltersShowClick?: () => void;
}

const BillboardPageContent = (props: Props) => {
  if (!props.area && !props.fav) {
    return <div className="text-center my-5"><MDSpinner singleColor="var(--primary)" /></div>;
  }

  if (props.fav && !props.isUserLogged) {
    return <RequireAuthMessage icon='fa-clapperboard'>
      <p>Inicia sesión para guardar tus cines y ver qué está por comenzar.</p>
    </RequireAuthMessage>;
  }

  if (props.fav && !props.favCinemasIds.length) {
    return <NoContentMessage icon='fa-clapperboard' title='Aún no tienes cines en "Mis cines"'>
      <p>Agrega tus favoritos para ver su cartelera y horarios.</p>
      <Button block onClick={props.onCinemasEdit}>Agregar cines</Button>
    </NoContentMessage>;
  }

  let cinemas;
  if (props.fav) {
    cinemas = props.favCinemasIds.map(id => props.cinemasById[id]).filter(Boolean);
  } else if (props.area) {
    cinemas = Object.values(props.cinemasById).filter(cinema => 'area' === props.area.type
      ? cinema.area.id === props.area.id
      : cinema.state.id === props.area.id); 
  } else {
    cinemas = [];
  }

  cinemas.sort((a, b) => a.name.localeCompare(b.name));

  const cinema = props.cinemasById && props.selectedCinemaId ? props.cinemasById[props.selectedCinemaId] : null;
  
  return (
    <React.Fragment>
      {props.area && (
        <Helmet>
          <title>Cartelera de {props.area.name} - Cinemex'</title>
          <link rel="canonical" href={getAbsoluteUrl(props.areaType === 'state' ? getStateBillboardPermalink(props.area) : getAreaBillboardPermalink(props.area))} />
        </Helmet>
      )}
      {props.fav && (
        <Helmet>
          <title>Cartelera de Mis cines - Cinemex'</title>
          <link rel="canonical" href={getAbsoluteUrl('/cartelera/mis-cines/')} />
        </Helmet>
      )}

      <div className='container d-none d-md-block'>
        <PageTitle title={props.area ? `Cartelera de ${props.area.name}` : 'Mis cines'} backTo='/' />
        {!props.area && (
          <div style={{margin:'-2rem 0 2rem', fontSize:'0.9rem'}} className='d-none d-md-block'>
            <a href="#" onClick={e => { e.preventDefault(); props.onCinemasEdit && props.onCinemasEdit(); }}>
              Agregar cine favorito
            </a>
          </div>
        )}
      </div>
      <BillboardStickyHeader>
        <div className=''>
          {props.area
            ? <BillboardHeading title={props.area.name} onAreaChangeClick={props.onAreaChangeClick} onFiltersShowClick={props.onFiltersShowClick} />
            : <BillboardHeading title='Mis cines' onCinemasEdit={props.onCinemasEdit} onFiltersShowClick={props.onFiltersShowClick} />}

          <div style={{ padding: `0 0 1rem var(--pad-x)`}}>
            <HorizontalScroller
              items={cinemas}
              selectedItemId={props.selectedCinemaId !== null ? props.selectedCinemaId : undefined}
              onSelect={id => props.onCinemaSelect(id)}
            />
            {!!cinema && 'maintenance' === cinema.status && <div style={{marginBottom:'calc(var(--pad-x) / 2 * -1)', marginRight:'var(--pad-x)'}}><CinemaMaintenanceLegend text={cinema.maintenance_message} /></div>}
          </div>
          <BillboardSidebarDates dates={props.dates} selectedDate={props.selectedDate} baseUrl='' onDateSelect={props.onDateSelect} />
        </div>
      </BillboardStickyHeader>

      {renderCinemasBlocks({ data: props.filteredData, isLoading: props.isLoading })}
    </React.Fragment>
  );
}

const renderCinemasBlocks = ({ data, isLoading }) => {
  if (!data || isLoading) {
    return <div className="text-center my-5"><MDSpinner singleColor="var(--primary)" /></div>;
  }
  
  if (!data.length) {
    return <NoContentMessage
      icon='fal fa-ticket'
      title="No hay películas disponibles con los filtros seleccionados."
    >
      <p>Puedes probar con otras opciones de filtrado.</p>
    </NoContentMessage>;
  }

  return data.map(cinema => <BillboardCinemaBlock key={cinema.id} cinema={cinema} movies={cinema.movies} showHeader={false} />);
}

export default BillboardPageContent;